import React, { useState, useEffect } from 'react';
import { X, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const AppBanner = () => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [platform, setPlatform] = useState('');

  useEffect(() => {
    // TODO Add Legacy Opera & Explorer Support
    const userAgent: string = navigator.userAgent;
    if (/android/i.test(userAgent)) {
      setPlatform('android');
    } else if (/iphone|ipad|ipod/i.test(userAgent)) {
      setPlatform('ios');
    } else {
      setPlatform('web');
    }
  }, []);

  if (!platform) return null;
  
  if (isMinimized) {
    return (
      <button
        onClick={() => setIsMinimized(false)}
        className="fixed top-2 right-2 bg-amber-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-800 transition-colors z-50 flex items-center gap-2"
      >
        <span className="w-6 h-6 bg-amber-200 rounded-full flex items-center justify-center">
          <span className="text-amber-900 font-bold text-xs">B4M</span>
        </span>
        Obtenir l&apos;app
      </button>
    );
  }

  const handleDownload = () => {
    if (platform === 'android') {
      window.location.href = 'https://play.google.com/store/apps/details?id=com.bible4me.app';
    } else if (platform === 'ios') {
      window.location.href = 'https://apps.apple.com/app/bible4me/id123456789';
    }
  };

  return (
    <div className="fixed top-0 left-0 right-0 bg-white shadow-md z-50">
      <div className="flex items-center p-3 max-w-6xl mx-auto">
        <div className="w-12 h-12 bg-amber-200 rounded-xl flex items-center justify-center mr-3">
          <span className="text-amber-900 font-bold text-lg">B4M</span>
        </div>

        <div className="flex-1">
          <h3 className="font-semibold text-amber-900">Bible4Me</h3>
          <div className="flex items-center gap-2">
            <div className="flex">
              {[1,2,3,4,5].map((_, i) => (
                <Star key={i} size={12} className="text-amber-400" />
              ))}
            </div>
            <span className="text-xs text-amber-800">Gratuit</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleDownload}
            className="bg-amber-900 text-white px-4 py-1.5 rounded-lg text-sm font-medium hover:bg-amber-800 transition-colors"
          >
            {platform === 'ios' ? 'Obtenir' : 'Installer'}
          </button>
          <button
            onClick={() => setIsMinimized(true)}
            className="p-1.5 text-amber-900 hover:bg-amber-100 rounded-full transition-colors"
            aria-label="Fermer"
          >
            <X size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

interface ErrorPageProps {
  title: string;
  message: string;
  technical?: string;
  onRetry?: () => void;
}

export const ErrorPage: React.FC<ErrorPageProps> = ({ title, message, technical, onRetry }) => {
  const { t } = useTranslation();
  
  useEffect(() => {
    // Add meta tag for iOS Smart App Banner
    const metaTag = document.createElement('meta');
    metaTag.name = 'apple-itunes-app';
    metaTag.content = 'app-id=123456789';
    document.head.appendChild(metaTag);

    return () => {
      document.head.removeChild(metaTag);
    };
  }, []);

  return (
    <>
      <AppBanner />
      <div className="min-h-screen bg-amber-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg max-w-lg w-full p-6">
          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 bg-red-100 rounded-full">
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-8 w-8 text-red-600" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          
          <h2 className="text-2xl font-bold text-center text-red-700 mb-4">
            {title}
          </h2>
          
          <p className="text-gray-700 text-center mb-6">
            {message}
          </p>
          
          {technical && (
            <details className="mb-6">
              <summary className="text-sm cursor-pointer text-gray-500">
                {t('errors.technicalDetails') || "Technical details"}
              </summary>
              <div className="text-xs mt-2 p-2 rounded font-mono overflow-auto max-h-32 bg-gray-100 text-gray-600">
                {technical}
              </div>
            </details>
          )}
          
          <div className="flex justify-center">
            <button
              onClick={onRetry || (() => window.history.back())}
              className="px-4 py-2 bg-amber-700 hover:bg-amber-800 text-white rounded-lg"
            >
              {onRetry ? t('common.retry') : t('common.goBack') || "Go Back"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ErrorPage;